/*
 * av-buybox.js — Avanelle native buy box controller (WP1)
 * -----------------------------------------------------------------------------
 * Externalized, idempotent replacement for the section's former inline <script>
 * IIFEs. Loaded once (defer) and persists across no-reload flavor swaps.
 *
 *  - AvBuyBox.mount(root)     wires one #av-buybox instance (gallery + zoom, tier
 *                            select, autoship toggle, ATC, sticky bar, nutrition
 *                            lightbox, bfcache reset). Idempotent — guarded by
 *                            root.dataset.avMounted; safe to call after each swap.
 *  - AvBuyBox.teardown(root)  resets page-level side effects (body overflow /
 *                            paddingBottom) a swap could otherwise strand.
 *
 * Page-level listeners (Escape, gallery arrow-keys, offer-CTA smooth scroll,
 * sticky scroll/resize, bfcache pageshow) are bound ONCE and always resolve the
 * CURRENT #av-buybox at event time — so swapping the node never accumulates
 * duplicate handlers or holds stale references (audit R1/R2/R3).
 *
 * Per-product values (locale format strings, routes, ids, price) are read from
 * the <script type="application/json" data-av-config> island inside each root,
 * so a Section-Rendering-API-swapped sibling carries its own config.
 */
(function () {
  'use strict';
  if (window.AvBuyBox) return;

  var GLOBALS_BOUND = false;
  var _lastFocus = null;

  function cfgOf(root) {
    var el = root && root.querySelector('[data-av-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }
  function currentRoot() { return document.getElementById('av-buybox'); }

  /* ------------------------------- focus trap ------------------------------ */
  /* Only one modal (gallery OR nutrition) is ever open at a time, so a single
     module-level saved-focus is sufficient. */
  function trapFocus(modal) {
    _lastFocus = document.activeElement;
    var f = modal.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
    if (f.length) f[0].focus();
    modal.__trap = function (e) {
      if (e.key !== 'Tab' || !f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    modal.addEventListener('keydown', modal.__trap);
  }
  function releaseFocus(modal) {
    if (modal && modal.__trap) { modal.removeEventListener('keydown', modal.__trap); modal.__trap = null; }
    if (_lastFocus && _lastFocus.focus) _lastFocus.focus();
    _lastFocus = null;
  }

  /* ------------------------- gallery + zoom lightbox ----------------------- */
  function mountGallery(root, cfg) {
    var COUNTER_FMT = cfg.counterFmt || '{{ current }} / {{ total }}';
    function fmtCounter(cur, tot) { return String(COUNTER_FMT).replace('__CUR__', cur).replace('__TOT__', tot); }

    /* The live gallery's parts. bindGallery() below (re)assigns them — on mount, and
       again when Berry Mix swaps in another product's gallery — and show() and the
       lightbox read them at call time, so both always act on the gallery on screen. */
    var imgs = [], thumbs = [], counter = null;
    var idx = 0;
    function show(n) {
      if (!imgs.length) return;
      idx = (n + imgs.length) % imgs.length;
      imgs.forEach(function (im, i) { im.classList.toggle('is-active', i === idx); });
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
      if (counter) counter.textContent = fmtCounter(idx + 1, imgs.length);
      var at = thumbs[idx];
      if (at && at.scrollIntoView) at.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    /* click-to-open lightbox with real pan/zoom */
    var gModal = root.querySelector('[data-gallery-modal]');
    var gZoom = root.querySelector('[data-gallery-zoom-img]');
    var gCounter = root.querySelector('[data-gallery-modal-counter]');
    var zs = 1, ztx = 0, zty = 0;
    function applyZoom() { if (gZoom) { gZoom.style.transform = 'translate(' + ztx + 'px,' + zty + 'px) scale(' + zs + ')'; gZoom.style.cursor = zs > 1 ? 'grab' : 'zoom-in'; } }
    function resetZoom() { zs = 1; ztx = 0; zty = 0; if (gZoom) gZoom.style.transition = 'none'; applyZoom(); }
    function clampPan() { if (!gZoom) return; var mx = gZoom.clientWidth * (zs - 1) / 2, my = gZoom.clientHeight * (zs - 1) / 2; ztx = Math.max(-mx, Math.min(mx, ztx)); zty = Math.max(-my, Math.min(my, zty)); }
    function zoomAt(factor, cx, cy) {
      if (!gZoom) return;
      var r = gZoom.getBoundingClientRect();
      var ox = cx - (r.left + r.width / 2), oy = cy - (r.top + r.height / 2);
      var ns = Math.max(1, Math.min(4, zs * factor)); var k = ns / zs;
      ztx = ox - (ox - ztx) * k; zty = oy - (oy - zty) * k; zs = ns;
      if (zs === 1) { ztx = 0; zty = 0; } clampPan();
      if (gZoom) gZoom.style.transition = 'transform .12s ease'; applyZoom();
    }
    function closeG() { if (gModal) { gModal.hidden = true; document.body.style.overflow = ''; resetZoom(); releaseFocus(gModal); } }
    function syncModalImage() { var a = imgs[idx]; if (a && gZoom) gZoom.src = a.currentSrc || a.src; resetZoom(); if (gCounter) gCounter.textContent = fmtCounter(idx + 1, imgs.length); }
    function modalNav(d) { show(idx + d); syncModalImage(); }
    function openG() { if (!imgs.length || !gModal || !gZoom) return; gModal.hidden = false; document.body.style.overflow = 'hidden'; syncModalImage(); trapFocus(gModal); }

    if (gModal && gZoom) {
      gModal.querySelectorAll('[data-gallery-close]').forEach(function (b) { b.addEventListener('click', closeG); });
      var gmPrev = gModal.querySelector('[data-gallery-modal-prev]');
      var gmNext = gModal.querySelector('[data-gallery-modal-next]');
      if (gmPrev) gmPrev.addEventListener('click', function (e) { e.stopPropagation(); modalNav(-1); });
      if (gmNext) gmNext.addEventListener('click', function (e) { e.stopPropagation(); modalNav(1); });
      var _swx = null;
      gZoom.addEventListener('touchstart', function (e) { _swx = (zs === 1 && e.touches.length === 1) ? e.touches[0].clientX : null; }, { passive: true });
      gZoom.addEventListener('touchend', function (e) { if (_swx === null || zs !== 1) { _swx = null; return; } var dx = e.changedTouches[0].clientX - _swx; if (Math.abs(dx) > 50) modalNav(dx < 0 ? 1 : -1); _swx = null; }, { passive: true });
      gZoom.addEventListener('wheel', function (e) { e.preventDefault(); zoomAt(e.deltaY < 0 ? 1.18 : 0.85, e.clientX, e.clientY); }, { passive: false });
      gZoom.addEventListener('dblclick', function (e) { e.preventDefault(); if (zs > 1) resetZoom(); else zoomAt(2.5, e.clientX, e.clientY); });
      var dragging = false, lx = 0, ly = 0, pts = {}, pinchD = 0;
      gZoom.addEventListener('pointerdown', function (e) {
        pts[e.pointerId] = { x: e.clientX, y: e.clientY }; var n = Object.keys(pts).length;
        if (n === 1) { dragging = zs > 1; lx = e.clientX; ly = e.clientY; try { gZoom.setPointerCapture(e.pointerId); } catch (_) {} }
        else if (n === 2) { dragging = false; var p = Object.keys(pts).map(function (k) { return pts[k]; }); pinchD = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y); }
      });
      gZoom.addEventListener('pointermove', function (e) {
        if (pts[e.pointerId]) pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var keys = Object.keys(pts);
        if (keys.length === 2) { var p = keys.map(function (k) { return pts[k]; }); var d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y); if (pinchD) zoomAt(d / pinchD, (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2); pinchD = d; }
        else if (dragging) { ztx += e.clientX - lx; zty += e.clientY - ly; lx = e.clientX; ly = e.clientY; clampPan(); if (gZoom) gZoom.style.transition = 'none'; applyZoom(); }
      });
      function endPtr(e) { delete pts[e.pointerId]; if (Object.keys(pts).length < 2) pinchD = 0; if (!Object.keys(pts).length) dragging = false; }
      gZoom.addEventListener('pointerup', endPtr);
      gZoom.addEventListener('pointercancel', endPtr);
    }

    /* Per-gallery controls: thumbs, arrows, swipe, and click-to-zoom on the stage.
       `scope` is the root on mount, then each gallery useGallery() inserts. Those are
       always fresh clones, so no element is ever bound twice. The lightbox above is
       bound once and outlives every swap. */
    function bindGallery(scope) {
      imgs = [].slice.call(scope.querySelectorAll('.av-gallery__img'));
      thumbs = [].slice.call(scope.querySelectorAll('.av-gallery__thumb'));
      counter = scope.querySelector('[data-gallery-counter]');
      idx = 0;
      thumbs.forEach(function (t) { t.addEventListener('click', function () { show(+t.dataset.idx); }); });
      var prev = scope.querySelector('[data-gallery-prev]');
      var next = scope.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', function () { show(idx - 1); });
      if (next) next.addEventListener('click', function () { show(idx + 1); });

      /* swipe on the main stage */
      var stage = scope.querySelector('[data-gallery-stage]');
      if (stage) {
        var x0 = null;
        stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          if (x0 === null) return;
          var dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
          x0 = null;
        }, { passive: true });
      }
      if (stage && gModal && gZoom) {
        stage.addEventListener('click', openG);
        stage.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openG(); } });
      }
    }
    bindGallery(root);

    root.__avGallery = {
      closeG: closeG,
      modalNav: modalNav,
      galleryModalOpen: function () { return !!(gModal && !gModal.hidden); },
      /* Puts `el` (a detached [data-gallery]) where the current one is and wires it up. */
      useGallery: function (el) {
        var cur = root.querySelector('[data-gallery]');
        if (!cur || !el) return;
        cur.replaceWith(el);
        bindGallery(el);
      }
    };
  }

  /* ----------------------------- Berry Mix view ---------------------------- */
  /* The duo is Strawberry + Raspberry, so while it is selected the page stops being the
     flavor it was opened on: the <h1> becomes the localized Berry Mix title
     (cfg.duoTitle), and the gallery, the sticky bar's thumbnail and the description
     paragraph become Strawberry's.
     Strawberry's markup comes from its own PDP through the Section Rendering API: every
     flavor PDP renders this same section, so the photos and copy are filtered for
     language and market exactly as that page shows them, and nothing is duplicated in
     Liquid. The request goes through av-flavor-swap.js's cache, which already prefetches
     the Strawberry tile at idle. cfg.duoGalleryUrl is only emitted when the page is not
     Strawberry itself — there, the gallery and paragraph already fit and only the title
     changes. Any fetch failure leaves the page's own gallery and paragraph. */
  function mountDuoView(root, cfg) {
    var gal = root.__avGallery;
    var own = root.querySelector('[data-gallery]');
    var url = (gal && gal.useGallery && own) ? cfg.duoGalleryUrl : null;
    var titleEl = root.querySelector('.av-bb__title');
    var duoTitle = cfg.duoTitle;
    if (!url && !(titleEl && duoTitle)) { root.__avDuoView = null; return; }
    own = own ? own.cloneNode(true) : null;       /* pristine copy to come back to */
    var ownTitle = titleEl ? titleEl.textContent : null;
    var introEl = root.querySelector('.av-bb__intro');
    var ownIntro = introEl ? introEl.innerHTML : null;
    var thumb = root.querySelector('.av-sticky__thumb');
    var ownThumb = thumb ? thumb.getAttribute('src') : null;
    var duo = null, pending = null, want = false, showing = false;

    function load() {
      if (duo) return Promise.resolve(duo);
      if (pending) return pending;
      var sw = window.AvFlavorSwap;
      var html = (sw && sw.fetchSection) ? sw.fetchSection(url)
        : fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(root.getAttribute('data-bb')), { credentials: 'same-origin' })
            .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); });
      pending = html.then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var g = doc.querySelector('#av-buybox [data-gallery]');
        if (!g) throw new Error('no [data-gallery] in the Strawberry section');
        var t = doc.querySelector('#av-buybox .av-sticky__thumb');
        var i = doc.querySelector('#av-buybox .av-bb__intro');
        duo = { gallery: g, thumb: t ? t.getAttribute('src') : null, intro: i ? i.innerHTML : null };
        return duo;
      });
      pending.catch(function () { pending = null; });  /* allow a retry on the next click */
      return pending;
    }
    /* The gallery, thumbnail and paragraph move together — they are the one fetch. */
    function put(g, src, intro) {
      if (g) gal.useGallery(g.cloneNode(true));
      if (thumb && src) thumb.setAttribute('src', src);
      if (introEl && intro != null) introEl.innerHTML = intro;
    }
    /* `want` is checked again when the fetch lands: a shopper who has already left
       Berry Mix (or swapped flavor, detaching this root) must not get Strawberry. */
    function set(on) {
      /* The title needs no fetch, so it changes on the same frame as the panel. */
      if (titleEl && duoTitle) titleEl.textContent = on ? duoTitle : ownTitle;
      if (!url) return;
      want = on;
      if (!on) { if (showing) { put(own, ownThumb, ownIntro); showing = false; } return; }
      load().then(function (d) {
        if (!want || showing || !root.isConnected) return;
        put(d.gallery, d.thumb, d.intro);
        showing = true;
      }).catch(function () {});
    }

    var mixTile = url && root.querySelector('[data-flavor-mix]');
    if (mixTile) {
      var warm = function () { load().catch(function () {}); };
      mixTile.addEventListener('pointerenter', warm);
      mixTile.addEventListener('touchstart', warm, { passive: true });
      mixTile.addEventListener('focus', warm);
    }
    root.__avDuoView = { set: set };
  }

  /* ---------------------------- nutrition lightbox ------------------------- */
  function mountNutrition(root) {
    var nModal = root.querySelector('[data-nutrition-modal]');
    function closeN() { if (nModal) { nModal.hidden = true; document.body.style.overflow = ''; releaseFocus(nModal); } }
    if (nModal) {
      root.querySelectorAll('[data-nutrition-open]').forEach(function (b) {
        b.addEventListener('click', function () { nModal.hidden = false; document.body.style.overflow = 'hidden'; trapFocus(nModal); });
      });
      nModal.querySelectorAll('[data-nutrition-close]').forEach(function (b) { b.addEventListener('click', closeN); });
    }
    root.__avNutrition = { closeN: closeN };
  }

  /* ------------------- tier select + autoship + ATC + OTP ------------------ */
  function mountOffer(root, cfg) {
    var bundle = root.querySelector('[data-av-bundle]');
    if (!bundle) { root.__avOffer = null; return; }
    var SAVE_FMT = cfg.saveFmt || 'SAVE {{ percent }}%';
    var SUB = cfg.sub || {};
    var OTP = cfg.otp || {};
    /* tier -> mode -> field, computed in Liquid (see snippets/av-buybox-prices.liquid).
       Every string here is already localized and already formatted for the active
       market's currency: this file does no money math and parses no money string. */
    var PRICES = (cfg.prices && cfg.prices.tiers) || {};
    /* Duo rows carry their own island entry (duo2/duo4/duo6), rendered by the section
       rather than by av-buybox-prices, which is shared with sections that have no duo
       panel. Merged into the same map so every reader below stays a single lookup.
       Absent on every section without duo, leaving PRICES exactly as it was. */
    if (cfg.duoPrices) {
      for (var dk in cfg.duoPrices) {
        if (Object.prototype.hasOwnProperty.call(cfg.duoPrices, dk)) PRICES[dk] = cfg.duoPrices[dk];
      }
    }
    /* The flavor packs (packs1 arm B, snippets/av-flavor-packs.liquid), merged the same way. */
    if (cfg.packPrices) {
      for (var fk in cfg.packPrices) {
        if (Object.prototype.hasOwnProperty.call(cfg.packPrices, fk)) PRICES[fk] = cfg.packPrices[fk];
      }
    }
    /* Discount-depth A/B test (snippets/av-exp.liquid). The section ships BOTH arms'
       Autoship prices: `subB` in the island, and [data-av-exp-a]/[data-av-exp-b]
       twins of each row's price, per-day and SAVE elements, one of which CSS hides
       until now. Here the visitor's arm becomes the only one: arm B's prices replace
       `sub` in the island, the other arm's elements are removed, and every reader
       below (setMode, the sticky bar, fitBundle) sees one plain set as before.
       No arm stamped (test off, or the stamp failed) keeps arm A — today's store.
       `armedB` is per-row: only a row that actually SHOWS an arm-B price claims the
       arm-B offer at checkout, so a section rendered without arm-B prices can never
       be charged a price it did not display. */
    var EXP = window.avExp || null;
    var ARM_B = !!(EXP && EXP.arm === 'b');
    var ARM_B_OFFER = 'creatine-gummies-depth-b';  /* = the bundle app's claim-only offer */
    var armedB = {};
    if (ARM_B) {
      for (var ek in PRICES) {
        if (Object.prototype.hasOwnProperty.call(PRICES, ek) && PRICES[ek] && PRICES[ek].subB) {
          PRICES[ek].sub = PRICES[ek].subB;
          armedB[ek] = true;
        }
      }
    }
    [].slice.call(bundle.querySelectorAll(ARM_B ? '[data-av-exp-a]' : '[data-av-exp-b]')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    [].slice.call(bundle.querySelectorAll('[data-av-exp-a], [data-av-exp-b]')).forEach(function (el) {
      el.removeAttribute('data-av-exp-a');
      el.removeAttribute('data-av-exp-b');
    });
    /* Buy-box A/B tests, stage 1 (snippets/av-pack.liquid): two independent tests.
       offer1 (Test 1): the 1-bottle row is [data-av-offer-only="a"], the 1+1 row
       [data-av-offer-only="b"]. price1 (Test 3): every total is [data-av-price-a], its
       per-bottle twin [data-av-price-b]. Here the visitor's arms become the only ones, on the
       whole section (rows, one-time link, sticky bar), and with price1 = b the per-bottle
       prices (subC/otpC) replace sub/otp in the island so setMode and syncOtp repaint per
       bottle too. No stamp = A for that test, today's store. */
    var TESTS = window.avTests || {};
    var OFFER_ARM = TESTS.offer1 === 'b' ? 'b' : 'a';
    var PRICE_ARM = TESTS.price1 === 'b' ? 'b' : 'a';
    var PACK_OFFER = 'creatine-gummies-pack-1plus1';  /* = the bundle app's claim-only offer */
    [].slice.call(root.querySelectorAll(OFFER_ARM === 'a' ? '[data-av-offer-only="b"]' : '[data-av-offer-only="a"]')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    [].slice.call(root.querySelectorAll(PRICE_ARM === 'b' ? '[data-av-price-a]' : '[data-av-price-b]')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    [].slice.call(root.querySelectorAll('[data-av-offer-only], [data-av-price-a], [data-av-price-b]')).forEach(function (el) {
      el.removeAttribute('data-av-offer-only');
      el.removeAttribute('data-av-price-a');
      el.removeAttribute('data-av-price-b');
    });
    /* packs1 (flavor packs): arm B keeps the Packs switch and panel and loses the Berry Mix
       tile and panel; arm A the reverse. Before the panel refs below are read, so in arm B
       the duo code sees no duo at all. */
    var PACKS_ARM = TESTS.packs1 === 'b' ? 'b' : 'a';
    [].slice.call(root.querySelectorAll(PACKS_ARM === 'a' ? '[data-av-packs-only="b"]' : '[data-av-packs-only="a"]')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    [].slice.call(root.querySelectorAll('[data-av-packs-only]')).forEach(function (el) {
      el.removeAttribute('data-av-packs-only');
    });
    if (PRICE_ARM === 'b') {
      for (var pk in PRICES) {
        if (Object.prototype.hasOwnProperty.call(PRICES, pk) && PRICES[pk] && PRICES[pk].subC) {
          PRICES[pk].sub = PRICES[pk].subC;
          if (PRICES[pk].otpC) PRICES[pk].otp = PRICES[pk].otpC;
        }
      }
    }
    /* order1 = b (tier order): the rows go into [data-av-order-rank] order, 1 bottle first.
       The CSS in av-pack already showed that order on first paint; moving the nodes makes
       Tab and screen readers follow it too. Same parent, same slot, so the panels and the
       pre-selected row are untouched. */
    if (TESTS.order1 === 'b') {
      /* Per parent: the single-flavor ladder and the flavor packs (packs1 = b) each have
         their own ranks, and a row must never move into the other's panel. */
      var hosts = [];
      [].slice.call(bundle.querySelectorAll('.av-bundle__row[data-av-order-rank]')).forEach(function (row) {
        if (hosts.indexOf(row.parentNode) < 0) hosts.push(row.parentNode);
      });
      hosts.forEach(function (host) {
        var ranked = [].slice.call(host.children).filter(function (el) { return el.matches('.av-bundle__row[data-av-order-rank]'); });
        if (ranked.length < 2) return;
        var anchor = ranked[ranked.length - 1].nextSibling;
        ranked.sort(function (x, y) {
          return (+x.getAttribute('data-av-order-rank')) - (+y.getAttribute('data-av-order-rank'));
        }).forEach(function (row) { host.insertBefore(row, anchor); });
      });
    }
    /* A removed row may have been the selected one; fall back to the first that is left. */
    if (!bundle.querySelector('.av-bundle__row[aria-pressed="true"]')) {
      var firstRow = bundle.querySelector('.av-bundle__row');
      if (firstRow) firstRow.setAttribute('aria-pressed', 'true');
    }

    /* Attribution on every line this buy box adds: "depth1:a|b" while that test runs, plus
       each running stage-1 test, e.g. "offer1:a,price1:b", and ",qa" for a QA visit. */
    var EXP_TAG = [EXP ? EXP.id + ':' + EXP.arm : '']
      .concat(['offer1', 'price1', 'order1', 'packs1', 'slider1'].map(function (id) { return TESTS[id] ? id + ':' + TESTS[id] : ''; }))
      .concat([window.avTestsQa ? 'qa' : ''])
      .filter(Boolean).join(',');
    if (EXP_TAG === 'qa') EXP_TAG = '';

    var ROOT_URL = cfg.rootUrl || '/';
    var CART_ADD_URL = cfg.cartAddUrl || '/cart/add.js';
    var CHECKOUT_URL = (ROOT_URL === '/' ? '' : ROOT_URL) + '/checkout';
    /* settings.pair_offer_enabled: the entry pack opens the cart slider instead of going
       straight to checkout. The single bottle finds the second-bottle offer there; the 1+1
       (offer1 = b) opens the same slider, so Test 1 differs only in the offer. */
    var CART_SLIDER = !!cfg.cartSlider;
    var variantId = bundle.getAttribute('data-variant');

    var rows = [].slice.call(bundle.querySelectorAll('.av-bundle__row'));

    /* Duo panel (Strawberry + Raspberry). All four refs are null when the section did not
       render a duo panel — which is every section other than the PDP buy box, and the PDP
       buy box in a market missing either component — so every duo function below is a
       guarded no-op there. */
    var panelSingle = bundle.querySelector('[data-av-panel="single"]');
    var panelDuo = bundle.querySelector('[data-av-panel="duo"]');
    var mixTile = root.querySelector('[data-flavor-mix]');
    var curTile = root.querySelector('[data-flavor-current]');
    var selectorEl = root.querySelector('.av-flavor-selector');
    /* The single panel's server-rendered default, captured before anything can move the
       selection, so leaving duo lands back on exactly the rung the page opened on. */
    var singleDefaultRow = panelSingle ? panelSingle.querySelector('.av-bundle__row[aria-pressed="true"]') : null;

    /* Content-aware compaction (see section CSS `.av-bundle.is-compact`).
       The row can distort only when its single-line content doesn't fit — which happens
       for long-digit currencies (e.g. MXN "$ 2,566" / "$ 5,345") on narrow viewports, not
       at any fixed width. We measure the actual rendered layout with images shown; if a
       title is forced onto a second line, we hide the decorative bottle images so the
       price + title stay on one line. Short-price markets never trip it. Idempotent:
       every call re-measures from the roomy state, so it settles without oscillating. */
    function rowWraps(row) {
      var t = row.querySelector('.av-bundle__title');
      if (!t) return false;
      var lh = parseFloat(getComputedStyle(t).lineHeight);
      if (!lh || isNaN(lh)) lh = parseFloat(getComputedStyle(t).fontSize) * 1.25;
      return t.getBoundingClientRect().height > lh * 1.6; /* > ~1 line */
    }
    function fitBundle() {
      bundle.classList.remove('is-compact');       /* measure the roomy layout */
      var tight = false;
      for (var i = 0; i < rows.length; i++) { if (rowWraps(rows[i])) { tight = true; break; } }
      bundle.classList.toggle('is-compact', tight);
    }

    function currentRow() { return bundle.querySelector('.av-bundle__row[aria-pressed="true"]') || rows[0]; }

    /* -------------------------- OTP text link sync -------------------------- */
    /* text_link preset only — [data-otp-link] doesn't exist on the switch preset,
       so every function below is a guarded no-op there. */
    var otpLink = root.querySelector('[data-otp-link]');
    var otpLabelText = null, otpErrorEl = root.querySelector('[data-otp-error]');
    var otpWasEl, otpNowEl, otpDayEl, otpLabelEl;
    if (otpLink) {
      otpLabelEl = otpLink.querySelector('[data-otp-label]');
      otpWasEl = otpLink.querySelector('[data-otp-was]');
      otpNowEl = otpLink.querySelector('[data-otp-now]');
      otpDayEl = otpLink.querySelector('[data-otp-day]');
      otpLabelText = otpLabelEl ? otpLabelEl.textContent : '';
    }
    /* The rows show subscription prices; the link shows the same tier one-time. Both
       come from the island, so nothing here re-reads a rendered price out of the DOM.
       `day` is the bare money string because the link wraps it in its own sentence —
       "(x / day)" — rather than the row's. */
    function syncOtp() {
      if (!otpLink) return;
      var row = currentRow();
      if (!row) return;
      var tier = PRICES[row.getAttribute('data-tier')];
      var p = tier && tier.otp;
      if (!p) return;
      var hasDiscount = p.save !== 0;
      if (otpWasEl) { otpWasEl.textContent = p.was; otpWasEl.hidden = !hasDiscount; }
      if (otpNowEl) otpNowEl.textContent = p.now;
      if (otpDayEl) otpDayEl.textContent = p.day ? String(OTP.perDayFmt || '__PRICE__').replace('__PRICE__', p.day) : '';
    }

    /* aria-pressed is the ONE source of truth for selection — it is what the CSS
       matches ([aria-pressed="true"]) and what every reader queries. There used to
       be a parallel .is-selected class; two signals for one piece of state can
       drift, and the accessible one is the one that must always be right. */
    function select(row) {
      rows.forEach(function (r) {
        r.setAttribute('aria-pressed', r === row ? 'true' : 'false');
      });
      syncOtp();
    }
    rows.forEach(function (r) {
      r.addEventListener('click', function () { select(r); });
      r.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(r); } });
    });

    /* ------------------------------- duo panel ------------------------------- */
    /* Switching panels ALWAYS resets to that panel's own default row — there is no
       remembered per-panel tier. Both panels live in the same [data-av-bundle], so
       select() above already clears aria-pressed across all of them. */
    function showPanel(showDuo) {
      if (!panelDuo) return;
      if (panelSingle) panelSingle.hidden = showDuo;
      panelDuo.hidden = !showDuo;
      var fallback = showDuo
        ? (panelDuo.querySelector('[data-duo-default]') || panelDuo.querySelector('.av-bundle__row'))
        : (singleDefaultRow || (panelSingle && panelSingle.querySelector('.av-bundle__row')));
      if (fallback) select(fallback);
      if (selectorEl) selectorEl.classList.toggle('is-mix', showDuo);
      /* Section-level too: the PDP buy box repaints in the Berry Mix gradient while this
         is set (see "Berry Mix (duo) accent" in that section's CSS), and mountDuoView
         takes the page's title, gallery and description with it. */
      root.classList.toggle('is-mix', showDuo);
      if (root.__avDuoView) root.__avDuoView.set(showDuo);
      if (mixTile) mixTile.setAttribute('aria-pressed', showDuo ? 'true' : 'false');
      /* The page's own flavor tile is an inert <div> while it IS the selection; in duo
         mode it becomes the way back, so it gains a button role and a tab stop. */
      if (curTile) {
        if (showDuo) { curTile.setAttribute('tabindex', '0'); curTile.setAttribute('role', 'button'); }
        else { curTile.removeAttribute('role'); curTile.setAttribute('tabindex', '-1'); }
      }
      fitBundle();          /* the visible row set changed — re-measure what fits */
      if (root.__avSticky) root.__avSticky.syncSticky();
    }
    if (mixTile && panelDuo) {
      mixTile.addEventListener('click', function () { showPanel(!!panelDuo.hidden); });
    }
    if (curTile && panelDuo) {
      curTile.addEventListener('click', function () { if (!panelDuo.hidden) showPanel(false); });
      curTile.addEventListener('keydown', function (e) {
        if (panelDuo.hidden || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        showPanel(false);
      });
    }

    /* ------------------------- packs switch (packs1 = b) ------------------------ */
    /* "Single flavor | Packs": Packs hides the flavor tiles and the single panel and shows
       the pack rows, selecting [data-pack-default]; back lands on the single panel's
       server default, like leaving Berry Mix. No-op where the switch was not rendered or
       was removed (arm A). */
    var panelPacks = bundle.querySelector('[data-av-panel="packs"]');
    var modeBtns = [].slice.call(root.querySelectorAll('[data-av-mode]'));
    function showPacks(on) {
      if (!panelPacks) return;
      if (panelSingle) panelSingle.hidden = on;
      panelPacks.hidden = !on;
      if (selectorEl) selectorEl.hidden = on;
      var fallback = on
        ? (panelPacks.querySelector('[data-pack-default]') || panelPacks.querySelector('.av-bundle__row'))
        : (singleDefaultRow || (panelSingle && panelSingle.querySelector('.av-bundle__row')));
      if (fallback) select(fallback);
      root.classList.toggle('is-packs', on);
      modeBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', (b.getAttribute('data-av-mode') === 'packs') === on ? 'true' : 'false');
      });
      fitBundle();
      if (root.__avSticky) root.__avSticky.syncSticky();
    }
    if (panelPacks) {
      modeBtns.forEach(function (b) {
        b.addEventListener('click', function () { showPacks(b.getAttribute('data-av-mode') === 'packs'); });
      });
    }

    var sub = root.querySelector('[data-av-sub]');
    var subBtn = sub && sub.querySelector('[data-sub-toggle]');
    var confirm = sub && sub.querySelector('[data-sub-confirm]');
    var lost = sub && sub.querySelector('[data-sub-lost]');
    var subTitle = sub && sub.querySelector('[data-sub-title]');
    var subDesc = sub && sub.querySelector('[data-sub-desc]');
    /* Repaints every row from the price island (display only — the strings were all
       computed server-side, in every mode, before first paint). */
    function setMode(mode) {
      if (!sub) return;
      sub.setAttribute('data-mode', mode);
      var on = mode === 'subscribe';
      if (subBtn) subBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (subTitle) subTitle.textContent = on ? SUB.title : SUB.onetimeTitle;
      if (subDesc) subDesc.textContent = on ? SUB.desc : SUB.onetimeDesc;
      var key = on ? 'sub' : 'otp';
      rows.forEach(function (r) {
        var tier = PRICES[r.getAttribute('data-tier')];
        var p = tier && tier[key];
        if (!p) return;
        var nowEl = r.querySelector('.av-bundle__now');
        var dayEl = r.querySelector('.av-bundle__sub');
        var saveEl = r.querySelector('.av-bundle__save');
        var wasEl = r.querySelector('.av-bundle__was');
        if (nowEl) nowEl.textContent = p.now;
        if (dayEl) dayEl.textContent = p.dayText;
        var hasDiscount = p.save !== 0;
        /* [hidden], never style.display — an inline element style beats every stylesheet
           selector for that property, which is what forces the !important wars we are
           removing. syncSticky() below READS this signal; the two must stay in sync. */
        if (saveEl) { saveEl.hidden = !hasDiscount; if (hasDiscount) saveEl.textContent = String(SAVE_FMT).replace('__PCT__', p.save); }
        if (wasEl) { wasEl.textContent = p.was; wasEl.hidden = !hasDiscount; }
      });
      /* Reactive opt-out panel (frictionless flavor): reveal + strike off the forfeited
         benefits when leaving Autoship; hide (and reset the strike) when back on. Kept out
         of the a11y tree while subscribed via [hidden]; a forced reflow lets the staggered
         line-through transition run on reveal. */
      if (lost) {
        if (on) { lost.classList.remove('is-open'); lost.hidden = true; }
        else { lost.hidden = false; void lost.offsetWidth; lost.classList.add('is-open'); }
      }
      fitBundle();  /* one-time prices have no discount → longer strings can change what fits */
    }
    if (sub && subBtn && lost) {
      /* Frictionless: the switch flips instantly in BOTH directions — no gate. Turning
         Autoship off reveals the reactive loss panel (setMode); re-up flips it back on. */
      subBtn.addEventListener('click', function () {
        setMode(sub.getAttribute('data-mode') === 'subscribe' ? 'onetime' : 'subscribe');
      });
      var reup = sub.querySelector('[data-sub-reup]');
      if (reup) reup.addEventListener('click', function () { setMode('subscribe'); });
    } else if (sub && subBtn) {
      /* Legacy opt-out gate (home buy box): confirm before dropping the subscription. */
      subBtn.addEventListener('click', function () {
        if (sub.getAttribute('data-mode') === 'subscribe') { if (confirm) confirm.hidden = false; }
        else setMode('subscribe');
      });
      var keep = sub.querySelector('[data-sub-keep]');
      var drop = sub.querySelector('[data-sub-drop]');
      if (keep) keep.addEventListener('click', function () { if (confirm) confirm.hidden = true; });
      if (drop) drop.addEventListener('click', function () { setMode('onetime'); if (confirm) confirm.hidden = true; });
    }

    /* --------------------------- shared cart-add POST -------------------------- */
    /* Builds the selected tier's paid (+free) line items. subscribe=true (main ATC
       when subscribed, or always on the text_link preset, whose tiers are
       subscription-only) attaches selling_plan; subscribe=false (switch preset in
       one-time mode, or the OTP link) omits it — everything else about the payload
       is identical. */
    /* The offer handle every duo row belongs to. Single source of truth for the _av_offer
       claim below — it must stay identical to the offer id authored in the compiled offer
       document (bundle-engine, sibling repo). Unlike _av_bundle, which is the row's visible
       localized title, this is a stable language-invariant handle. */
    var DUO_OFFER_HANDLE = 'creatine-gummies-berries-duo';
    /* Arm B of the discount-depth test: a duo row that SHOWS an arm-B price (armedB,
       above) claims the bundle app's claim-only duo offer instead. It carries arm A's
       one-time rates, so a one-time B line is charged what it displays too. */
    var DUO_ARM_B_OFFER = 'creatine-gummies-berries-duo-depth-b';
    function buildItems(row, subscribe) {
      var plan = row.getAttribute('data-plan') || '';
      var titleEl = row.querySelector('.av-bundle__title');
      var offer = titleEl ? titleEl.textContent.trim() : 'Bundle';
      var bundleId = 'av' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);

      /* Duo rows carry data-lines (a JSON array of {id, qty}) instead of data-paid /
         data-free, because a duo tier buys two DIFFERENT products: one paid cart line per
         component, both sharing this add's single _av_bundle_id. 1+1 -> 2 paid units,
         2+2 -> 4, 3+3 -> 6, and there is deliberately no _av_free line — the pricing
         engine rejects a mixmatch group that contains one, and those unit totals must
         equal the engine bars' `paid` values or selectBar discards the claim entirely. */
      var linesAttr = row.getAttribute('data-lines');
      if (linesAttr) {
        var lines;
        try { lines = JSON.parse(linesAttr); } catch (e) { return []; }
        var duoTier = row.getAttribute('data-tier') || '';
        return lines.map(function (l) {
          var props = { _av_bundle: offer, _av_bundle_id: bundleId, _av_plan: plan };
          if (EXP_TAG) props._av_exp = EXP_TAG;
          /* _av_tier and _av_offer are what the pricing engine identifies this row from.
             They are SELECTORS, NEVER VALUES. The engine re-derives the offer from each
             line's OWN product / variant / collection membership and the tier from the
             quantity it actually observes in the cart, then uses these two only to
             disambiguate among what already matched (selectOffer / selectBar in
             extensions/bundle-engine, sibling repo). A forged or stale value can pick
             among outcomes the cart already earns; it can never add an offer or upgrade a
             tier. So do not "optimize" later by trusting them client-side, and do not move
             enforcement here — this is an honest claim, not the enforcement point.
             duoTier is the row's own data-tier (duo2 | duo4 | duo6), which is exactly how
             the engine's bars are authored; do not invent new ids here. */
          if (duoTier) {
            props._av_tier = duoTier;
            /* A flavor pack row names its own offer (data-offer); a duo row's is the duo's. */
            props._av_offer = row.getAttribute('data-offer') || (armedB[duoTier] ? DUO_ARM_B_OFFER : DUO_OFFER_HANDLE);
          }
          var item = { id: Number(l.id), quantity: Number(l.qty) || 1, properties: props };
          if (subscribe && plan) item.selling_plan = Number(plan);
          return item;
        });
      }

      /* --- single-flavor path: unchanged --- */
      var paid = parseInt(row.getAttribute('data-paid'), 10) || 1;
      var free = parseInt(row.getAttribute('data-free'), 10) || 0;
      var items = [];
      var paidItem = { id: Number(variantId), quantity: paid, properties: { _av_bundle: offer, _av_bundle_id: bundleId, _av_plan: plan } };
      if (subscribe && plan) paidItem.selling_plan = Number(plan);
      /* Arm B: claim the claim-only offer (bundle-engine prices it on Autoship only;
         a one-time line carries the claim so a later cart-side switch to Autoship
         still lands on arm B). A SELECTOR like every other claim — the engine still
         re-derives the product and the paid quantity. Tier id mirrors its bars. */
      if (armedB[row.getAttribute('data-tier')]) {
        paidItem.properties._av_offer = ARM_B_OFFER;
        paidItem.properties._av_tier = 'b' + paid;
      }
      /* Test 1's 1+1 (offer1 = b): claim the claim-only offer, Autoship and one-time
         alike; its bar `p2` carries both prices. A SELECTOR like every other claim. */
      if (row.getAttribute('data-tier') === 'p11') {
        paidItem.properties._av_offer = PACK_OFFER;
        paidItem.properties._av_tier = 'p2';
      }
      if (EXP_TAG) paidItem.properties._av_exp = EXP_TAG;
      items.push(paidItem);
      if (free > 0) {
        var freeItem = { id: Number(variantId), quantity: free, properties: { _av_bundle: offer, _av_bundle_id: bundleId, _av_free: 'true' } };
        if (EXP_TAG) freeItem.properties._av_exp = EXP_TAG;
        items.push(freeItem);
      }
      return items;
    }
    /* Re-render the cart slider with the cart as it is now, then open it. Returns null when
       there is no slider on this page (cart_type "page"), so the caller falls back to checkout. */
    function openCartSlider() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.refreshCartDrawer !== 'function' || typeof drawer.toggleDrawerClasses !== 'function') return null;
      return Promise.resolve(drawer.refreshCartDrawer()).then(function () {
        if (!drawer.isOpen) drawer.toggleDrawerClasses();
      });
    }
    /* Cart slider test, arm B (slider1 = b, snippets/av-pack.liquid): EVERY add opens the
       slider at once, with a loader, and the slider fills in when the add lands; nothing goes
       straight to checkout. Null for arm A / no test, and where there is no slider. */
    function sliderNow() {
      if (TESTS.slider1 !== 'b') return null;
      var drawer = document.querySelector('cart-drawer');
      return drawer && typeof drawer.openWhileAdding === 'function' ? drawer.openWhileAdding() : null;
    }
    function submitAdd(items, btn, onError, onSlider) {
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      function release() { btn.disabled = false; btn.removeAttribute('aria-busy'); }
      var early = sliderNow();
      return fetch(CART_ADD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) { if (early) early.fail(); release(); onError(res.description || res.message); return; }
          /* A/B test: pin the visitor's arm on the cart before leaving the page, so the
             arm outlives Safari's cookie cap and reaches the order (snippets/av-pack.liquid,
             av-exp.liquid). One write after the other, never together: cart attribute writes
             are last-write-wins, and in parallel one of the two was lost on 5 of 8 tries,
             sometimes the landing page's lp_view (av-ad-head wraps avExpRemember to write it).
             Never blocks the add: each resolves on failure too. */
          return Promise.resolve(window.avTestsRemember ? window.avTestsRemember() : null)
            .then(function () { return window.avExpRemember ? window.avExpRemember() : null; })
            .then(function () { return res; });
        })
        .then(function (res) {
          if (!res) return;
          if (early) {
            return early.done().then(function () { release(); if (onSlider) onSlider(); });
          }
          /* The entry pack → the slider: one bottle, or the 1+1. Anything bigger (2+1, 3+2,
             duo) keeps going straight to checkout, unless this cart has been to checkout
             already (snippets/av-pack.liquid): then every add opens the slider. */
          var first = items.length === 1 ? items[0] : null;
          var entry = !!first && (Number(first.quantity) === 1 || (first.properties && first.properties._av_offer === PACK_OFFER));
          var back = !!(window.avCheckoutSeen && window.avCheckoutSeen());
          var slider = (CART_SLIDER && entry) || back ? openCartSlider() : null;
          if (!slider) { if (window.avCheckoutMark) window.avCheckoutMark(); window.location.href = CHECKOUT_URL; return; }
          slider.then(function () { release(); if (onSlider) onSlider(); },
                      function () { if (window.avCheckoutMark) window.avCheckoutMark(); window.location.href = CHECKOUT_URL; });
        })
        .catch(function (err) { if (early) early.fail(); release(); onError(err); });
    }

    /* Real Add to Cart. On the switch preset, subscribe follows the toggle; on the
       text_link preset there is no toggle (sub is null) and tiers are
       subscription-only, so it defaults to true — the one-time path is the OTP
       link below, not this button. */
    var atcBtn = root.querySelector('[data-bundle-atc]');
    if (atcBtn && variantId) {
      atcBtn.addEventListener('click', function () {
        var row = currentRow();
        if (!row) return;
        var subscribe = sub ? sub.getAttribute('data-mode') === 'subscribe' : true;
        submitAdd(buildItems(row, subscribe), atcBtn, function (err) { console.warn('[av-bundle] add error:', err); });
      });
    }

    /* One-Time Purchase text link (text_link preset only) — same payload as the
       button minus selling_plan, same checkout redirect, disables + shows a loading
       label while in flight, re-enables with an inline localized error on failure
       without auto-retry. */
    if (otpLink && variantId) {
      otpLink.addEventListener('click', function () {
        var row = currentRow();
        if (!row) return;
        if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
        if (otpLabelEl && OTP.adding) otpLabelEl.textContent = OTP.adding;
        submitAdd(buildItems(row, false), otpLink, function () {
          if (otpLabelEl) otpLabelEl.textContent = otpLabelText;
          if (otpErrorEl) { otpErrorEl.textContent = OTP.error || ''; otpErrorEl.hidden = false; }
        }, resetOtp);
      });
    }

    /* Back-nav / bfcache recovery for the OTP button. The click handler above leaves it
       disabled with the "Adding…" label because the tab is on its way to /checkout; when
       Safari restores this page from bfcache (back button) that in-flight state comes back
       with it and reads as a stuck spinner. Called from __avBfcache on every pageshow —
       a no-op when the button is already at rest, and when there is no OTP link at all. */
    function resetOtp() {
      if (!otpLink) return;
      otpLink.disabled = false;
      otpLink.removeAttribute('aria-busy');
      if (otpLabelEl && otpLabelText != null) otpLabelEl.textContent = otpLabelText;
      if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
    }

    /* Selection carry-over across a flavor swap (paid quantity + autoship mode, when
       the switch preset's mode exists to carry). */
    root.__avOffer = {
      fit: fitBundle,
      resetOtp: resetOtp,
      getSelection: function () {
        /* Scoped to the single panel when one exists: a flavor swap must land the sibling
           on a sensible single-flavor rung, never on a duo row (whose data-paid is null
           anyway, and whose components are fixed regardless of the flavor swapped to).
           Swapping FROM duo mode therefore reports no rung, and the sibling keeps its own
           server-rendered default — same as a first visit. */
        var r = (panelSingle || bundle).querySelector('.av-bundle__row[aria-pressed="true"]');
        return { paid: r ? r.getAttribute('data-paid') : null, tier: r ? r.getAttribute('data-tier') : null, mode: sub ? sub.getAttribute('data-mode') : null };
      },
      applySelection: function (sel) {
        if (!sel) return;
        if (sel.paid != null) {
          /* The tier id first: 1+1 and 2+1 share a paid quantity (2). */
          var target = (sel.tier && bundle.querySelector('.av-bundle__row[data-tier="' + sel.tier + '"]')) ||
            bundle.querySelector('.av-bundle__row[data-paid="' + sel.paid + '"]');
          if (target) select(target);
        }
        if (sel.mode && sub && sel.mode !== sub.getAttribute('data-mode')) setMode(sel.mode);
      }
    };

    syncOtp();    /* initial paint already server-renders the default tier's OTP price; this just wires it up */
    fitBundle();  /* initial pass; re-run once the webfont settles (metrics shift wrapping) */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitBundle);
  }

  /* ------------------------------ sticky ATC ------------------------------- */
  function mountSticky(root) {
    var sticky = root.querySelector('[data-av-sticky]');
    var bundle = root.querySelector('[data-av-bundle]');
    var atcMain = root.querySelector('[data-bundle-atc]');
    if (!sticky || !bundle) { root.__avSticky = null; return; }
    var rows = [].slice.call(bundle.querySelectorAll('.av-bundle__row'));
    var offerEl = sticky.querySelector('[data-sticky-offer]');
    var nowEl = sticky.querySelector('[data-sticky-now]');
    var wasEl = sticky.querySelector('[data-sticky-was]');
    function barH() { return sticky.offsetHeight || 64; }
    function reserve() { document.body.style.paddingBottom = barH() + 'px'; }
    function syncSticky() {
      var row = bundle.querySelector('.av-bundle__row[aria-pressed="true"]') || rows[0];
      if (!row) return;
      var t = row.querySelector('.av-bundle__title');
      var now = row.querySelector('.av-bundle__now');
      var was = row.querySelector('.av-bundle__was');
      if (offerEl && t) offerEl.textContent = t.textContent.trim();
      if (nowEl && now) nowEl.textContent = now.textContent.trim();
      if (wasEl) {
        /* [hidden], never style.display — see the row-price block above. On the switch
           preset this reads the signal setMode() writes on the row's .av-bundle__was;
           on the text_link preset there is no setMode() price-swap, so it is whatever
           Liquid server-rendered and never changes here. */
        var showWas = was && !was.hidden && was.textContent.trim();
        wasEl.textContent = showWas ? was.textContent.trim() : '';
        wasEl.hidden = !showWas;
      }
    }
    function place() {
      var visible = atcMain ? (atcMain.getBoundingClientRect().bottom < 0) : false;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    /* Re-sync after any tier / subscribe interaction (per-root, dies with the node). */
    root.addEventListener('click', function (e) { if (e.target.closest('.av-bundle__row, [data-av-sub]')) requestAnimationFrame(syncSticky); });
    root.addEventListener('keyup', function (e) { if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('.av-bundle__row')) requestAnimationFrame(syncSticky); });
    var stickyBtn = sticky.querySelector('[data-sticky-atc]');
    if (stickyBtn && atcMain) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled || atcMain.disabled) return;
        stickyBtn.disabled = true;
        atcMain.click();
        setTimeout(function () { stickyBtn.disabled = false; }, 1500);
      });
    }
    reserve(); syncSticky(); place();
    root.__avSticky = { place: place, reserve: reserve, syncSticky: syncSticky };
  }

  /* --------------------------- bfcache recovery ---------------------------- */
  function mountBfcache(root) {
    var atc = root.querySelector('[data-bundle-atc]');
    var sticky = root.querySelector('[data-sticky-atc]');
    /* Resting disabled state = server render (true only when sold out). */
    var atcResting = atc ? atc.disabled : false;
    var stickyResting = sticky ? sticky.disabled : false;
    root.__avBfcache = function (e) {
      if (atc) { atc.disabled = atcResting; atc.removeAttribute('aria-busy'); }
      if (sticky) { sticky.disabled = stickyResting; sticky.removeAttribute('aria-busy'); }
      /* Resolved at call time so a flavor swap's fresh __avOffer is the one reset. */
      if (root.__avOffer && root.__avOffer.resetOtp) root.__avOffer.resetOtp();
      if (e && e.persisted) {
        var cd = document.querySelector('cart-drawer');
        if (cd && typeof cd.refreshCartDrawer === 'function') cd.refreshCartDrawer();
      }
    };
  }

  /* ------------------- offer-CTA smooth scroll (delegated) ----------------- */
  function topOffset() {
    var bottom = 0;
    document.querySelectorAll('header, sticky-header, [class*="header"], [class*="announcement"]').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        var r = el.getBoundingClientRect();
        if (r.top <= 2 && r.height > 8 && r.height < 240 && r.bottom > bottom) bottom = r.bottom;
      }
    });
    return bottom > 0 ? bottom + 12 : 16;
  }
  function glide(toY, dur) {
    var startY = window.pageYOffset, diff = toY - startY, t0 = null;
    function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
    function step(ts) { if (t0 === null) t0 = ts; var p = Math.min((ts - t0) / dur, 1); window.scrollTo(0, startY + diff * ease(p)); if (p < 1) requestAnimationFrame(step); }
    requestAnimationFrame(step);
  }
  function offerCtaHandler(e) {
    var a = e.target.closest('a[href$="#av-buybox"], a[href$="#av-offer"]');
    if (!a) return;
    var target = document.getElementById('av-offer') || document.querySelector('.av-bundle') || document.getElementById('av-buybox');
    if (!target) return;
    e.preventDefault();
    var y = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - topOffset());
    var dist = Math.abs(y - window.pageYOffset);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || dist < 4) { window.scrollTo(0, y); return; }
    glide(y, Math.max(320, Math.min(640, dist * 0.45)));
  }

  /* --------------------------- global listeners ---------------------------- */
  function bindGlobalsOnce() {
    if (GLOBALS_BOUND) return;
    GLOBALS_BOUND = true;
    document.addEventListener('keydown', function (e) {
      var root = currentRoot();
      if (!root) return;
      if (e.key === 'Escape') {
        if (root.__avGallery) root.__avGallery.closeG();
        if (root.__avNutrition) root.__avNutrition.closeN();
        return;
      }
      var g = root.__avGallery;
      if (g && g.galleryModalOpen()) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); g.modalNav(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); g.modalNav(1); }
      }
    });
    document.addEventListener('click', offerCtaHandler, false);
    window.addEventListener('scroll', function () { var r = currentRoot(); if (r && r.__avSticky) r.__avSticky.place(); }, { passive: true });
    window.addEventListener('resize', function () { var r = currentRoot(); if (!r) return; if (r.__avSticky) { r.__avSticky.reserve(); r.__avSticky.place(); } if (r.__avOffer && r.__avOffer.fit) r.__avOffer.fit(); }, { passive: true });
    window.addEventListener('pageshow', function (e) { var r = currentRoot(); if (r && r.__avBfcache) r.__avBfcache(e); });
  }

  /* -------------------------------- mount ---------------------------------- */
  function mount(root) {
    if (!root || root.dataset.avMounted === '1') return;
    root.dataset.avMounted = '1';
    var cfg = cfgOf(root);
    mountGallery(root, cfg);
    mountDuoView(root, cfg);
    mountNutrition(root);
    mountOffer(root, cfg);
    mountSticky(root);
    mountBfcache(root);
    bindGlobalsOnce();
  }
  function teardown(root) {
    document.body.style.overflow = '';
    document.body.style.paddingBottom = '';
    _lastFocus = null;
    if (root) root.dataset.avMounted = '';
  }

  window.AvBuyBox = { mount: mount, teardown: teardown, config: cfgOf };

  function boot() { [].slice.call(document.querySelectorAll('[data-bb]')).forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
